import { useEffect, useMemo, useRef, useState } from "react";
import api, { extractErr } from "../lib/api";
import { Plus, X, Search, MessageSquare, LayoutGrid, Table as TableIcon, Tag as TagIcon, Send, Upload, Pencil, Check } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["New", "Contact", "Consider", "Register", "Lost"];

const STAGE_LABELS = {
  New: "全新 NEW",
  Contact: "沟通中",
  Consider: "考虑报名",
  Register: "成功报名",
  Lost: "没回复",
};

const stageAccent = {
  New: "border-slate-300 bg-slate-50",
  Contact: "border-blue-300 bg-blue-50",
  Consider: "border-amber-300 bg-amber-50",
  Register: "border-emerald-300 bg-emerald-50",
  Lost: "border-red-300 bg-red-50",
};

const stageBadge = (s) => ({
  New: "bg-slate-100 text-slate-700 border-slate-300",
  Contact: "bg-blue-50 text-blue-800 border-blue-200",
  Consider: "bg-amber-50 text-amber-800 border-amber-200",
  Register: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Lost: "bg-red-50 text-red-800 border-red-200",
}[s] || "bg-slate-100 text-slate-700");

const PRESET_TAG_FILTERS = ["P1-核心高价值", "SEL兴趣", "AI Bundle买家", "TUP买家"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState("kanban");
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [stateFilter, setStateFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [meta, setMeta] = useState({ states: [], job_roles: [], client_categories: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [showBulkSend, setShowBulkSend] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = () => api.get("/leads").then(r => setLeads(r.data));

  const patchLead = async (id, fields) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, fields);
      setLeads(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));
      return data;
    } catch (e) { toast.error(extractErr(e)); throw e; }
  };
  useEffect(() => {
    load();
    api.get("/lead-meta").then(r => setMeta(r.data));
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    leads.forEach(l => (l.tags || []).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [leads]);

  const filtered = useMemo(() => {
    return leads.filter(l => {
      if (activeTagFilter && !(l.tags || []).includes(activeTagFilter)) return false;
      if (stateFilter && l.state !== stateFilter) return false;
      if (roleFilter && l.job_role !== roleFilter) return false;
      if (categoryFilter && l.client_category !== categoryFilter) return false;
      if (stageFilter && l.stage !== stageFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (l.name || "").toLowerCase().includes(s)
        || (l.phone || "").includes(s)
        || (l.interest || "").toLowerCase().includes(s)
        || (l.organization || "").toLowerCase().includes(s)
        || (l.title || "").toLowerCase().includes(s)
        || (l.last_inquired_course || "").toLowerCase().includes(s)
        || (l.state || "").toLowerCase().includes(s)
        || (l.tags || []).some(t => t.toLowerCase().includes(s));
    });
  }, [leads, q, activeTagFilter, stateFilter, roleFilter, categoryFilter, stageFilter]);

  const move = async (id, stage) => {
    try {
      await api.patch(`/leads/${id}`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(l => l.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const patchTags = async (id, tags) => {
    try {
      await api.patch(`/leads/${id}`, { tags });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const addTagToLead = (lead, tag) => {
    const clean = (tag || "").trim();
    if (!clean) return;
    const current = lead.tags || [];
    if (current.includes(clean)) return;
    patchTags(lead.id, [...current, clean]);
  };

  const removeTagFromLead = (lead, tag) => {
    patchTags(lead.id, (lead.tags || []).filter(t => t !== tag));
  };

  const selectedLeads = leads.filter(l => selectedIds.has(l.id));

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">Pipeline · Contacts</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">Leads</h1>
          <p className="text-slate-500 mt-1 text-sm">Kanban 跑销售进度 · Table 做批量打标签与群发</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex border border-slate-300 rounded-sm p-0.5 bg-white">
            <button
              data-testid="view-kanban-btn"
              onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 ${view === "kanban" ? "bg-slate-950 text-white" : "text-slate-600"}`}
            >
              <LayoutGrid size={13} /> Kanban
            </button>
            <button
              data-testid="view-table-btn"
              onClick={() => setView("table")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 ${view === "table" ? "bg-slate-950 text-white" : "text-slate-600"}`}
            >
              <TableIcon size={13} /> Table
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="leads-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name/phone/tag/org..."
              className="pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 w-full sm:w-64"
            />
          </div>
          <button
            data-testid="import-csv-btn"
            onClick={() => setShowImport(true)}
            className="px-3 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-sm hover:border-emerald-700 hover:text-emerald-700 flex items-center gap-1.5"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            data-testid="add-lead-btn"
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700 flex items-center gap-1.5"
          >
            <Plus size={15} /> New lead
          </button>
        </div>
      </div>

      {/* Tag filter chips */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mr-1">Filter:</div>
        <TagFilterChip label="全选 All" active={!activeTagFilter} onClick={() => setActiveTagFilter(null)} count={leads.length} testid="tag-filter-all" />
        {PRESET_TAG_FILTERS.map(t => (
          <TagFilterChip
            key={t}
            label={t}
            active={activeTagFilter === t}
            onClick={() => setActiveTagFilter(t)}
            count={leads.filter(l => (l.tags || []).includes(t)).length}
            testid={`tag-filter-${t}`}
          />
        ))}
        {allTags.filter(t => !PRESET_TAG_FILTERS.includes(t)).slice(0, 12).map(t => (
          <TagFilterChip
            key={t}
            label={t}
            active={activeTagFilter === t}
            onClick={() => setActiveTagFilter(t)}
            count={leads.filter(l => (l.tags || []).includes(t)).length}
            testid={`tag-filter-${t}`}
          />
        ))}
      </div>

      {/* Stage filter row · easy click on mobile */}
      <div className="flex items-center flex-wrap gap-1.5 mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mr-1">Stage:</div>
        {STAGES.map(s => {
          const count = leads.filter(l => l.stage === s).length;
          const active = stageFilter === s;
          return (
            <button
              key={s}
              data-testid={`stage-filter-${s}`}
              onClick={() => setStageFilter(active ? "" : s)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-sm border transition-all ${
                active ? stageBadge(s) + " ring-2 ring-offset-1 ring-slate-400" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              {STAGE_LABELS[s] || s} <span className="opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Advanced dimension filters */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mr-1">Dimension:</div>
        <select data-testid="state-filter" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
          <option value="">All states 全部州属</option>
          {meta.states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
          <option value="">All roles 全部岗位</option>
          {meta.job_roles.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
          <option value="">All B2C/B2B</option>
          {meta.client_categories.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(stateFilter || roleFilter || categoryFilter || activeTagFilter || stageFilter) && (
          <button data-testid="clear-filters" onClick={() => { setStateFilter(""); setRoleFilter(""); setCategoryFilter(""); setActiveTagFilter(null); setStageFilter(""); }} className="text-[10px] font-semibold text-emerald-700 hover:underline">清除全部筛选</button>
        )}
        <div className="ml-auto text-[10px] text-slate-500">显示 {filtered.length} / {leads.length}</div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div data-testid="bulk-action-bar" className="mb-4 flex items-center gap-3 p-3 bg-orange-50 border-2 border-orange-400 rounded-sm shadow-brutal-accent">
          <div className="text-sm font-semibold text-orange-900">
            已选中 <span data-testid="bulk-selected-count" className="text-orange-700">{selectedIds.size}</span> 个联系人
          </div>
          <button
            data-testid="bulk-tag-btn"
            onClick={() => setShowBulkTag(true)}
            className="px-3 py-1.5 bg-white border border-orange-500 text-orange-700 text-xs font-semibold rounded-sm hover:bg-orange-100 flex items-center gap-1.5"
          >
            <TagIcon size={12} /> 批量打标签
          </button>
          <button
            data-testid="bulk-send-btn"
            onClick={() => setShowBulkSend(true)}
            className="px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-sm hover:bg-orange-600 flex items-center gap-1.5"
          >
            <Send size={12} /> Bulk WhatsApp
          </button>
          <button
            data-testid="bulk-clear-btn"
            onClick={clearSelection}
            className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            清除选择
          </button>
        </div>
      )}

      {view === "kanban" ? (
        <KanbanView filtered={filtered} onMove={move} patchLead={patchLead} allTags={allTags} onAddTag={addTagToLead} onRemoveTag={removeTagFromLead} />
      ) : (
        <TableView
          rows={filtered}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onAddTag={addTagToLead}
          onRemoveTag={removeTagFromLead}
          allTags={allTags}
          patchLead={patchLead}
        />
      )}

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {showImport && <ImportCsvModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showBulkTag && (
        <BulkTagModal
          leadIds={Array.from(selectedIds)}
          onClose={() => setShowBulkTag(false)}
          onDone={() => { setShowBulkTag(false); clearSelection(); load(); }}
          existingTags={allTags}
        />
      )}
      {showBulkSend && (
        <BulkSendModal
          leads={selectedLeads}
          onClose={() => setShowBulkSend(false)}
          onDone={() => { setShowBulkSend(false); clearSelection(); load(); }}
        />
      )}
    </div>
  );
}

function TagFilterChip({ label, active, onClick, count, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-slate-950 text-white border-slate-950"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}
    >
      {label} <span className={active ? "text-slate-300" : "text-slate-400"}>{count}</span>
    </button>
  );
}

function KanbanView({ filtered, onMove, patchLead, allTags, onAddTag, onRemoveTag }) {
  const [editingId, setEditingId] = useState(null);
  const openChat = async (phone) => {
    const r = await api.get("/conversations");
    const conv = r.data.find(c => c.phone === phone);
    if (conv) window.location.href = `/inbox?c=${conv.id}`;
    else toast.error("No conversation yet for this lead");
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {STAGES.map(stage => {
        const items = filtered.filter(l => l.stage === stage);
        return (
          <div key={stage} className={`border ${stageAccent[stage]} rounded-sm min-h-[400px] flex flex-col`}>
            <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-white">
              <div className="text-[11px] tracking-[0.15em] font-bold text-slate-700">{STAGE_LABELS[stage] || stage}</div>
              <div className="text-xs font-mono text-slate-500">{items.length}</div>
            </div>
            <div className="p-2 space-y-2 flex-1 overflow-y-auto scrollbar-thin">
              {items.map(l => {
                const isEdit = editingId === l.id;
                return (
                <div key={l.id} data-testid={`lead-card-${l.id}`} className={`bg-white border ${isEdit ? "border-emerald-500 shadow-md" : "border-slate-200 hover:shadow-sm"} p-3 rounded-sm transition-shadow relative`}>
                  <button
                    data-testid={`edit-card-${l.id}`}
                    onClick={() => setEditingId(isEdit ? null : l.id)}
                    className={`absolute top-1.5 right-1.5 p-1 rounded-sm ${isEdit ? "bg-emerald-100 text-emerald-800" : "text-slate-400 hover:bg-slate-100 hover:text-emerald-700"}`}
                    title={isEdit ? "Done · 完成" : "Edit · 编辑"}
                  >
                    {isEdit ? <Check size={11} /> : <Pencil size={11} />}
                  </button>
                  {isEdit ? (
                    <div className="space-y-1.5 pr-6">
                      <InlineField testid={`kc-name-${l.id}`} value={l.name} onSave={(v) => patchLead(l.id, { name: v })} displayClassName="font-semibold text-sm" />
                      <InlineField testid={`kc-phone-${l.id}`} value={l.phone} onSave={(v) => patchLead(l.id, { phone: v })} displayClassName="text-[11px] font-mono text-slate-500" />
                      <InlineField testid={`kc-title-${l.id}`} value={l.title} onSave={(v) => patchLead(l.id, { title: v })} placeholder="+ Title" displayClassName="text-[11px] text-slate-600" />
                      <InlineField testid={`kc-org-${l.id}`} value={l.organization} onSave={(v) => patchLead(l.id, { organization: v })} placeholder="+ Organization" displayClassName="text-[11px] text-slate-600" />
                      <InlineField testid={`kc-course-${l.id}`} value={l.last_inquired_course} onSave={(v) => patchLead(l.id, { last_inquired_course: v })} placeholder="+ Course" displayClassName="text-[11px] text-slate-600" />
                      <InlineField testid={`kc-cat-${l.id}`} type="select" options={CATEGORY_OPTIONS} value={l.client_category} onSave={(v) => patchLead(l.id, { client_category: v })} placeholder="+ B2C/B2B" displayClassName="text-[10px]" />
                      <InlineField testid={`kc-stage-${l.id}`} type="select" options={STAGE_OPTIONS} value={l.stage} onSave={(v) => patchLead(l.id, { stage: v })} displayClassName={`inline-block text-[10px] font-bold tracking-wider px-2 py-0.5 border ${stageBadge(l.stage)}`} />
                      <div className="mt-1.5">
                        <InlineTagsEditor lead={l} onAdd={(t) => onAddTag(l, t)} onRemove={(t) => onRemoveTag(l, t)} suggestions={allTags} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold text-sm mb-0.5 pr-6">{l.name}</div>
                      <div className="text-[11px] font-mono text-slate-500 mb-1.5">{l.phone}</div>
                      {(l.organization || l.title) && (
                        <div className="text-[11px] text-slate-600 mb-1.5">
                          {l.title && <span>{l.title}</span>}
                          {l.title && l.organization && <span> · </span>}
                          {l.organization && <span>{l.organization}</span>}
                        </div>
                      )}
                      {(l.last_inquired_course || l.interest) && (
                        <div className="text-[11px] text-slate-600 mb-2">📚 {l.last_inquired_course || l.interest}</div>
                      )}
                      {(l.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(l.tags || []).slice(0, 3).map(t => (
                            <span key={t} className="text-[9px] font-semibold px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm">{t}</span>
                          ))}
                          {(l.tags || []).length > 3 && <span className="text-[9px] text-slate-400">+{(l.tags || []).length - 3}</span>}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {STAGES.filter(s => s !== l.stage).map(s => (
                          <button
                            key={s}
                            data-testid={`move-lead-${l.id}-${s}`}
                            onClick={() => onMove(l.id, s)}
                            className="text-[10px] font-semibold tracking-wide px-1.5 py-0.5 border border-slate-200 hover:border-emerald-700 hover:text-emerald-700 rounded-sm"
                          >→ {STAGE_LABELS[s] || s}</button>
                        ))}
                      </div>
                      <button
                        onClick={() => openChat(l.phone)}
                        className="mt-2 inline-flex items-center gap-1 text-[10px] text-emerald-700 hover:underline"
                      >
                        <MessageSquare size={10} /> Open chat
                      </button>
                    </>
                  )}
                </div>
                );
              })}
              {items.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6">Empty</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InlineField({ value, onSave, type = "text", options = [], placeholder = "—", className = "", displayClassName = "", testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.focus(), 0); }, [editing]);

  const commit = async () => {
    const newVal = draft === "" ? null : draft;
    if (newVal === value || (newVal == null && !value)) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(newVal); setEditing(false); }
    catch { setDraft(value || ""); }
    finally { setBusy(false); }
  };

  const cancel = () => { setDraft(value || ""); setEditing(false); };

  if (editing) {
    if (type === "select") {
      return (
        <select ref={inputRef} value={draft || ""} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          data-testid={testid} disabled={busy}
          className={`w-full px-1.5 py-0.5 text-xs border border-emerald-500 rounded-sm focus:outline-none bg-white ${className}`}>
          <option value="">—</option>
          {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      );
    }
    return (
      <input ref={inputRef} value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); else if (e.key === "Escape") cancel(); }}
        onBlur={commit} disabled={busy} data-testid={testid}
        className={`w-full px-1.5 py-0.5 text-xs border border-emerald-500 rounded-sm focus:outline-none ${className}`} />
    );
  }

  const label = value || placeholder;
  const isEmpty = !value;
  const optionLabel = type === "select" ? (options.find(o => (o.value ?? o) === value)?.label ?? value ?? placeholder) : label;
  return (
    <button type="button" data-testid={testid} onClick={() => setEditing(true)}
      className={`w-full text-left cursor-text hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 rounded-sm px-1 py-0.5 transition-colors ${isEmpty ? "text-slate-300" : ""} ${displayClassName}`}>
      {busy ? "…" : optionLabel}
    </button>
  );
}

const STAGE_OPTIONS = STAGES.map(s => ({ value: s, label: STAGE_LABELS[s] || s }));
const CATEGORY_OPTIONS = [
  { value: "", label: "—" },
  { value: "B2C 个人", label: "B2C 个人" },
  { value: "B2B 机构", label: "B2B 机构" },
];

function TableView({ rows, selectedIds, onToggleSelect, onToggleSelectAll, onAddTag, onRemoveTag, allTags, patchLead }) {
  const allChecked = rows.length > 0 && rows.every(r => selectedIds.has(r.id));
  const openChat = async (phone) => {
    const r = await api.get("/conversations");
    const conv = r.data.find(c => c.phone === phone);
    if (conv) window.location.href = `/inbox?c=${conv.id}`;
    else toast.error("No conversation yet");
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
              <th className="p-3 w-10">
                <input
                  data-testid="select-all-checkbox"
                  type="checkbox"
                  checked={allChecked}
                  onChange={onToggleSelectAll}
                  className="w-4 h-4 accent-emerald-700 cursor-pointer"
                />
              </th>
              <th className="p-3">Name / Title</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Organization</th>
              <th className="p-3">State / Role</th>
              <th className="p-3">B2C/B2B</th>
              <th className="p-3">Last Course</th>
              <th className="p-3">Stage</th>
              <th className="p-3 min-w-[220px]">Tags</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(l => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className={selectedIds.has(l.id) ? "bg-emerald-50/40" : "hover:bg-slate-50"}>
                <td className="p-3">
                  <input
                    data-testid={`row-checkbox-${l.id}`}
                    type="checkbox"
                    checked={selectedIds.has(l.id)}
                    onChange={() => onToggleSelect(l.id)}
                    className="w-4 h-4 accent-emerald-700 cursor-pointer"
                  />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-name-${l.id}`} value={l.name} onSave={(v) => patchLead(l.id, { name: v })} displayClassName="font-semibold" />
                  <InlineField testid={`edit-title-${l.id}`} value={l.title} onSave={(v) => patchLead(l.id, { title: v })} placeholder="+ Title" displayClassName="text-xs text-slate-500 mt-0.5" />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-phone-${l.id}`} value={l.phone} onSave={(v) => patchLead(l.id, { phone: v })} displayClassName="font-mono text-xs text-slate-700" />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-org-${l.id}`} value={l.organization} onSave={(v) => patchLead(l.id, { organization: v })} displayClassName="text-xs text-slate-700" placeholder="+ Organization" />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-state-${l.id}`} value={l.state} onSave={(v) => patchLead(l.id, { state: v })} displayClassName="text-xs text-slate-700" placeholder="+ State" />
                  <InlineField testid={`edit-role-${l.id}`} value={l.job_role} onSave={(v) => patchLead(l.id, { job_role: v })} displayClassName="text-[10px] text-slate-500 mt-0.5" placeholder="+ Role" />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-cat-${l.id}`} type="select" options={CATEGORY_OPTIONS} value={l.client_category} onSave={(v) => patchLead(l.id, { client_category: v })} displayClassName={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm inline-block ${(l.client_category || "").startsWith("B2B") ? "bg-purple-50 text-purple-800 border border-purple-200" : l.client_category ? "bg-blue-50 text-blue-800 border border-blue-200" : ""}`} placeholder="+ B2C/B2B" />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-course-${l.id}`} value={l.last_inquired_course} onSave={(v) => patchLead(l.id, { last_inquired_course: v })} displayClassName="text-xs text-slate-700" placeholder="+ Course" />
                </td>
                <td className="p-3">
                  <InlineField testid={`edit-stage-${l.id}`} type="select" options={STAGE_OPTIONS} value={l.stage} onSave={(v) => patchLead(l.id, { stage: v })} displayClassName={`inline-block text-[11px] font-bold tracking-wider px-2 py-0.5 border ${stageBadge(l.stage)}`} />
                </td>
                <td className="p-3">
                  <InlineTagsEditor
                    lead={l}
                    onAdd={(t) => onAddTag(l, t)}
                    onRemove={(t) => onRemoveTag(l, t)}
                    suggestions={allTags}
                  />
                </td>
                <td className="p-3">
                  <button
                    onClick={() => openChat(l.phone)}
                    className="text-emerald-700 hover:underline text-xs font-semibold"
                    data-testid={`row-open-chat-${l.id}`}
                  >
                    Open chat
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">No leads match your filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InlineTagsEditor({ lead, onAdd, onRemove, suggestions = [] }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState("");
  const submit = () => {
    if (val.trim()) onAdd(val.trim());
    setVal(""); setAdding(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-1">
      {(lead.tags || []).map(t => (
        <span key={t} data-testid={`tag-chip-${lead.id}-${t}`} className="group inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm">
          {t}
          <button
            data-testid={`tag-remove-${lead.id}-${t}`}
            onClick={() => onRemove(t)}
            className="opacity-0 group-hover:opacity-100 hover:text-red-600"
          ><X size={10} /></button>
        </span>
      ))}
      {adding ? (
        <input
          data-testid={`tag-input-${lead.id}`}
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={submit}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") { setVal(""); setAdding(false); }
          }}
          list={`tag-suggestions-${lead.id}`}
          placeholder="New tag..."
          className="text-[10px] w-24 px-1.5 py-0.5 border border-emerald-500 rounded-sm focus:outline-none"
        />
      ) : (
        <button
          data-testid={`tag-add-btn-${lead.id}`}
          onClick={() => setAdding(true)}
          className="text-[10px] px-1.5 py-0.5 border border-dashed border-slate-300 hover:border-emerald-500 hover:text-emerald-700 text-slate-500 rounded-sm"
        >
          <Plus size={10} className="inline" /> tag
        </button>
      )}
      <datalist id={`tag-suggestions-${lead.id}`}>
        {suggestions.map(s => <option key={s} value={s} />)}
      </datalist>
    </div>
  );
}

function BulkTagModal({ leadIds, onClose, onDone, existingTags = [] }) {
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);

  const addTag = (t) => {
    const clean = (t || "").trim();
    if (clean && !tags.includes(clean)) setTags([...tags, clean]);
    setTagInput("");
  };

  const submit = async () => {
    if (tags.length === 0) { toast.error("Add at least one tag"); return; }
    setBusy(true);
    try {
      await api.post("/leads/bulk-tag", { lead_ids: leadIds, tags });
      toast.success(`已为 ${leadIds.length} 个联系人加上 ${tags.length} 个标签`);
      onDone();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-md shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-orange-700 font-semibold">Bulk action</div>
            <div className="font-display text-lg font-bold">批量打标签 · {leadIds.length} contacts</div>
          </div>
          <button onClick={onClose} data-testid="bulk-tag-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block">Tags to add</label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-sm min-h-[40px] bg-slate-50">
              {tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm">
                  {t}
                  <button onClick={() => setTags(tags.filter(x => x !== t))}><X size={11} /></button>
                </span>
              ))}
              <input
                data-testid="bulk-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                placeholder="Type a tag and press Enter..."
                list="bulk-tag-suggestions"
                className="flex-1 min-w-[100px] text-xs bg-transparent focus:outline-none"
              />
              <datalist id="bulk-tag-suggestions">
                {existingTags.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">按 Enter 添加。现有标签会作为建议出现。</div>
          </div>
          <button
            data-testid="bulk-tag-submit"
            onClick={submit}
            disabled={busy || tags.length === 0}
            className="w-full py-2.5 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-40"
          >
            {busy ? "Adding…" : `Add ${tags.length} tag${tags.length === 1 ? "" : "s"} to ${leadIds.length} contacts`}
          </button>
        </div>
      </div>
    </div>
  );
}

const BULK_FIELDS = [
  { key: "name", label: "Name (名字)" },
  { key: "title", label: "Title (职位)" },
  { key: "organization", label: "Organization (机构)" },
  { key: "last_inquired_course", label: "Last Course (咨询课程)" },
  { key: "interest", label: "Interest (兴趣)" },
  { key: "stage", label: "Stage (阶段)" },
];

function BulkSendModal({ leads, onClose, onDone }) {
  const [mode, setMode] = useState("freeform"); // "freeform" | "template" | "wati_template"
  const [templates, setTemplates] = useState([]);
  const [watiTemplates, setWatiTemplates] = useState([]);
  const [watiLoading, setWatiLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedWati, setSelectedWati] = useState(null);
  const [mapping, setMapping] = useState({});
  const [freeformText, setFreeformText] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const textareaRef = useRef(null);

  // Tag filter (further narrow the selected leads)
  const [tagFilter, setTagFilter] = useState(new Set());
  const allTags = useMemo(() => {
    const s = new Set();
    leads.forEach(l => (l.tags || []).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [leads]);
  const filteredLeads = useMemo(() => {
    if (tagFilter.size === 0) return leads;
    return leads.filter(l => (l.tags || []).some(t => tagFilter.has(t)));
  }, [leads, tagFilter]);
  const toggleTag = (t) => {
    const n = new Set(tagFilter);
    n.has(t) ? n.delete(t) : n.add(t);
    setTagFilter(n);
  };

  // Image upload
  const [image, setImage] = useState(null); // { path, filename, previewUrl }
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Save-as-template dialog
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplCategory, setTplCategory] = useState("MARKETING");

  useEffect(() => {
    api.get("/whatsapp/templates").then(r => setTemplates(r.data));
  }, []);

  const loadWatiTemplates = async () => {
    setWatiLoading(true);
    try {
      const { data } = await api.get("/wati/templates");
      // Only APPROVED templates should be listed for sending
      const approved = (data.templates || []).filter(t => (t.status || "").toUpperCase() === "APPROVED" || t.source === "local");
      setWatiTemplates(approved);
      if (approved.length === 0) toast.info("YCloud 暂无已批准的模板，请先在 YCloud 提交审核");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setWatiLoading(false); }
  };
  useEffect(() => { if (mode === "wati_template" && watiTemplates.length === 0) loadWatiTemplates(); /* eslint-disable-next-line */ }, [mode]);

  const activeTemplate = mode === "wati_template" ? selectedWati : selected;
  const varCount = mode === "wati_template"
    ? (selectedWati ? Math.max(selectedWati.variables || 0, (selectedWati.body || "").match(/\{\{\d+\}\}/g)?.length || 0) : 0)
    : (selected?.variables || 0);

  const currentBody = mode === "template" ? (selected?.body || "")
    : mode === "wati_template" ? (selectedWati?.body || "")
    : freeformText;

  const previewText = useMemo(() => {
    const lead = filteredLeads[0];
    if (!currentBody) return "";
    let text = currentBody;
    if ((mode === "template" || mode === "wati_template") && lead) {
      for (let i = 1; i <= varCount; i++) {
        const field = mapping[String(i)];
        const val = field ? (lead[field] || `[空 ${field}]`) : `[未映射 var${i}]`;
        text = text.replace(new RegExp(`\\{\\{${i}\\}\\}`, "g"), val);
      }
    }
    if (lead) {
      text = text.replaceAll("{{姓名}}", lead.name || "").replaceAll("{{name}}", lead.name || "");
    }
    return text;
  }, [currentBody, mode, mapping, filteredLeads, varCount]);

  const insertNameVar = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = freeformText.slice(0, start) + "{{姓名}}" + freeformText.slice(end);
    setFreeformText(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + 6, start + 6);
    }, 0);
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("只支持图片文件"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("图片最大 5MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage({ path: data.path, filename: data.filename, previewUrl: URL.createObjectURL(f) });
      toast.success("图片已上传");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("请输入模板名称"); return; }
    try {
      await api.post("/whatsapp/templates", {
        name: tplName.trim(),
        language: "zh_CN",
        category: tplCategory,
        body: freeformText,
        status: "DRAFT",
      });
      toast.success("已保存为模板 · 可在下次使用");
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data);
      setShowSaveTpl(false);
      setTplName("");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const submit = async () => {
    if (mode === "template" && !selected) { toast.error("请选择模板"); return; }
    if (mode === "wati_template" && !selectedWati) { toast.error("请选择 WhatsApp 模板"); return; }
    if (mode === "freeform" && !freeformText.trim() && !image) { toast.error("请输入内容或上传图片"); return; }
    if (filteredLeads.length === 0) { toast.error("没有符合条件的收件人"); return; }
    if (mode === "template" || mode === "wati_template") {
      for (let i = 1; i <= varCount; i++) {
        if (!mapping[String(i)]) { toast.error(`请为 {{${i}}} 选择字段`); return; }
      }
    }
    setBusy(true);
    try {
      const rawPayload = {
        lead_ids: filteredLeads.map(l => l.id).filter(Boolean),
        mapping: (mode === "template" || mode === "wati_template") ? mapping : {},
        media_path: image?.path || null,
        mode,
      };
      if (mode === "template") {
        rawPayload.template_name = selected.name;
        if (typeof selected.language === "string") rawPayload.language = selected.language;
      } else if (mode === "wati_template") {
        rawPayload.wati_template_name = selectedWati.name;
        if (typeof selectedWati.language === "string") rawPayload.language = selectedWati.language;
      } else {
        rawPayload.body_override = freeformText;
      }
      if (scheduledFor) {
        const dt = new Date(scheduledFor);
        if (dt < new Date()) { toast.error("排程时间必须是未来时间"); setBusy(false); return; }
        rawPayload.scheduled_for = dt.toISOString();
      }
      // NORMALIZE: strip null / undefined / non-string from optional string fields, and stringify mapping values
      const payload = {};
      Object.entries(rawPayload).forEach(([k, v]) => {
        if (v === null || v === undefined) return;              // drop null keys → backend uses defaults
        if (k === "mapping" && v && typeof v === "object") {
          const clean = {};
          Object.entries(v).forEach(([mk, mv]) => {
            if (mv !== null && mv !== undefined && mv !== "") clean[String(mk)] = String(mv);
          });
          payload[k] = clean;
          return;
        }
        payload[k] = v;
      });
      // Debug print so user can inspect exact payload in browser DevTools
      // eslint-disable-next-line no-console
      console.log("[BULK-SEND] payload →", JSON.stringify(payload, null, 2));

      const { data } = await api.post("/whatsapp/bulk-send", payload);
      setJobId(data.job_id);
      if (data.status === "scheduled") {
        toast.success(`已排程 · ${data.total} 条 · ${new Date(data.scheduled_for).toLocaleString("zh-CN")}`);
      } else {
        toast.success(`已排入队列 · ${data.total} 条 · 每日剩余 ${(data.daily_cap || 0) - (data.daily_sent_before || 0)}`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[BULK-SEND] backend rejected →", e?.response?.data);
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const first = detail[0];
        toast.error(`字段 ${(first.loc || []).join(".")} · ${first.msg}`);
      } else {
        toast.error(extractErr(e));
      }
    }
    finally { setBusy(false); }
  };

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/whatsapp/bulk-jobs/${jobId}`);
        if (!mounted) return;
        setJob(data);
        if (["completed", "cancelled"].includes(data.status)) return; // stop
        setTimeout(tick, 2000);
      } catch (e) {
        if (mounted) setTimeout(tick, 4000);
      }
    };
    tick();
    return () => { mounted = false; };
  }, [jobId]);

  const resumeJob = async () => {
    try { await api.post(`/whatsapp/bulk-jobs/${jobId}/resume`); toast.success("已恢复发送"); }
    catch (e) { toast.error(extractErr(e)); }
  };
  const cancelJob = async () => {
    if (!confirm("确定取消这个群发任务？未发送的联系人将被跳过。")) return;
    try { await api.post(`/whatsapp/bulk-jobs/${jobId}/cancel`); toast.success("已取消"); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-orange-700 font-semibold">Bulk WhatsApp</div>
            <div className="font-display text-lg font-bold">群发 · {filteredLeads.length}/{leads.length} contacts</div>
          </div>
          <button onClick={onClose} data-testid="bulk-send-close"><X size={18} /></button>
        </div>
        {jobId ? (
          <div className="p-5 space-y-3">
            {job?.status === "paused" && (
              <div className="p-3 bg-red-50 border-2 border-red-400 rounded-sm" data-testid="bulk-pause-banner">
                <div className="text-xs font-bold text-red-900 uppercase tracking-wider mb-1">⚠️ 队列已自动暂停</div>
                <div className="text-sm text-red-800 leading-relaxed break-all">{job.pause_reason || "未知原因"}</div>
                <div className="flex gap-2 mt-2">
                  <button data-testid="bulk-resume-btn" onClick={resumeJob} className="px-3 py-1.5 bg-emerald-700 text-white text-xs font-semibold rounded-sm hover:bg-emerald-800">继续发送</button>
                  <button data-testid="bulk-cancel-btn" onClick={cancelJob} className="px-3 py-1.5 border border-red-500 text-red-700 text-xs font-semibold rounded-sm hover:bg-red-50">取消任务</button>
                </div>
              </div>
            )}

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-600 font-semibold">Job Status</div>
                <div className={`text-xs font-bold uppercase tracking-wider ${
                  job?.status === "running" ? "text-blue-700" :
                  job?.status === "paused" ? "text-red-700" :
                  job?.status === "completed" ? "text-emerald-700" :
                  job?.status === "cancelled" ? "text-slate-500" : "text-slate-500"
                }`} data-testid="bulk-job-status">
                  {job?.status || "starting…"}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center mb-3">
                <div><div className="text-2xl font-display font-black text-emerald-700" data-testid="bulk-sent">{job?.sent ?? 0}</div><div className="text-[10px] text-slate-500">已发送</div></div>
                <div><div className="text-2xl font-display font-black text-red-700" data-testid="bulk-failed">{job?.failed ?? 0}</div><div className="text-[10px] text-slate-500">失败</div></div>
                <div><div className="text-2xl font-display font-black text-amber-700" data-testid="bulk-skipped">{(job?.skipped_unsubscribed ?? 0) + (job?.skipped ?? 0)}</div><div className="text-[10px] text-slate-500">跳过</div></div>
                <div><div className="text-2xl font-display font-black text-slate-700">{job?.total ?? filteredLeads.length}</div><div className="text-[10px] text-slate-500">总数</div></div>
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-600 transition-all" style={{ width: `${job?.total ? Math.round(((job.sent + job.failed + (job.skipped_unsubscribed || 0) + (job.skipped || 0)) / job.total) * 100) : 0}%` }} />
              </div>
              {job?.cooling_down_until && (
                <div className="mt-2 text-[10px] text-blue-700 font-semibold">⏸ 冷却中 · 每 50 条暂停 60 秒</div>
              )}
              <div className="mt-2 text-[10px] text-slate-500">
                今日全局: {job?.daily_sent_today ?? 0} / {job?.daily_cap ?? 1000}
              </div>
            </div>

            {(job?.details || []).length > 0 && (
              <div>
                <div className="text-xs text-slate-600 mb-1">最近记录:</div>
                <div className="max-h-56 overflow-y-auto scrollbar-thin border border-slate-200 rounded-sm">
                  {(job.details || []).slice(-30).reverse().map((d, i) => (
                    <div key={i} className={`p-2 text-xs flex items-center justify-between border-b border-slate-100 ${
                      d.status === "ok" ? "" : d.status === "skipped" ? "bg-amber-50" : "bg-red-50"
                    }`}>
                      <span className="truncate mr-2">{d.name || d.lead_id}</span>
                      <span className={
                        d.status === "ok" ? "text-emerald-700" :
                        d.status === "skipped" ? "text-amber-700" : "text-red-700"
                      }>
                        {d.status === "ok" ? "✓" : d.status === "skipped" ? `⊘ ${d.reason || "skipped"}` : `✗ ${(d.error || "").slice(0, 60)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {["completed", "cancelled"].includes(job?.status) && (
              <button onClick={onDone} data-testid="bulk-send-done" className="w-full py-2.5 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700">Done</button>
            )}
            {job?.status === "running" && (
              <button data-testid="bulk-cancel-running" onClick={cancelJob} className="w-full py-2 border border-slate-300 text-slate-700 text-xs font-semibold rounded-sm hover:bg-slate-50">取消任务</button>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Mode toggle */}
            <div className="flex gap-2 border border-slate-200 rounded-sm p-1 bg-slate-50">
              <button
                data-testid="mode-freeform"
                onClick={() => setMode("freeform")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-sm ${mode === "freeform" ? "bg-white shadow-sm text-emerald-700" : "text-slate-500"}`}
              >📝 自由输入</button>
              <button
                data-testid="mode-wati-template"
                onClick={() => setMode("wati_template")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-sm ${mode === "wati_template" ? "bg-white shadow-sm text-purple-700" : "text-slate-500"}`}
              >🌟 官方模板</button>
              <button
                data-testid="mode-template"
                onClick={() => setMode("template")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-sm ${mode === "template" ? "bg-white shadow-sm text-emerald-700" : "text-slate-500"}`}
              >📋 本地模板</button>
            </div>

            {/* Tag filter for recipients */}
            {allTags.length > 0 && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block">按标签筛选收件人 (Tag Filter)</label>
                <div className="flex flex-wrap gap-1.5" data-testid="bulk-tag-filter">
                  {allTags.map(t => (
                    <button
                      key={t}
                      data-testid={`bulk-tag-${t}`}
                      onClick={() => toggleTag(t)}
                      className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${tagFilter.has(t) ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-500"}`}
                    >
                      {t}
                    </button>
                  ))}
                  {tagFilter.size > 0 && (
                    <button onClick={() => setTagFilter(new Set())} className="text-[10px] text-slate-500 hover:underline ml-1">清除</button>
                  )}
                </div>
                {tagFilter.size > 0 && (
                  <div className="text-[10px] text-emerald-700 mt-1">已过滤到 {filteredLeads.length} 人</div>
                )}
              </div>
            )}

            {/* Content editor */}
            {mode === "freeform" ? (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">消息内容 (Freeform)</label>
                  <div className="flex gap-1.5">
                    <button
                      data-testid="insert-name-var"
                      type="button"
                      onClick={insertNameVar}
                      className="text-[10px] px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-sm hover:bg-amber-200 font-semibold"
                    >+ 插入 {`{{姓名}}`}</button>
                    <button
                      data-testid="save-as-template"
                      type="button"
                      onClick={() => setShowSaveTpl(true)}
                      disabled={!freeformText.trim()}
                      className="text-[10px] px-2 py-1 bg-slate-100 text-slate-700 border border-slate-300 rounded-sm hover:bg-slate-200 font-semibold disabled:opacity-40"
                    >💾 保存为模板</button>
                  </div>
                </div>
                <textarea
                  ref={textareaRef}
                  data-testid="freeform-textarea"
                  value={freeformText}
                  onChange={(e) => setFreeformText(e.target.value)}
                  rows={5}
                  placeholder="老师您好！本月 SEL 认证班有会员福利，{{姓名}} 有兴趣了解详情吗？"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-y"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  发送时 <span className="font-mono">{`{{姓名}}`}</span> 会自动替换为每位联系人的真实姓名。
                </div>
                <div data-testid="freeform-24h-hint" className="text-[10px] text-amber-700 bg-amber-50 border-l-2 border-amber-400 px-2 py-1 mt-1 rounded-sm">
                  WhatsApp 规定：普通文字只能发给 24 小时内回复过您的联系人，其他人会被自动跳过。联系旧客户请用「官方模板」。
                </div>
              </div>
            ) : mode === "wati_template" ? (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-purple-700 font-semibold">YCloud 已批准模板 (实时)</label>
                  <button data-testid="reload-wati-tpl" onClick={loadWatiTemplates} disabled={watiLoading} className="text-[10px] px-2 py-0.5 border border-purple-300 text-purple-700 rounded-sm hover:bg-purple-50 disabled:opacity-40 font-semibold">
                    {watiLoading ? "载入中…" : "🔄 刷新"}
                  </button>
                </div>
                <select
                  data-testid="wati-tpl-select"
                  value={selectedWati?.name || ""}
                  onChange={(e) => {
                    const t = watiTemplates.find(t => t.name === e.target.value);
                    setSelectedWati(t || null);
                    setMapping({});
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700 bg-white"
                >
                  <option value="">-- 选择已批准模板 --</option>
                  {watiTemplates.map(t => (
                    <option key={t.name} value={t.name}>
                      {t.name} · {t.category} · {t.status} {t.source === "local" ? " (本地 fallback)" : ""}
                    </option>
                  ))}
                </select>
                {selectedWati && (
                  <div className="mt-2 p-2 bg-purple-50 border-l-2 border-purple-400 rounded-sm text-[11px] text-slate-700 whitespace-pre-wrap">
                    {selectedWati.body || "(模板内容不可预览)"}
                  </div>
                )}
                {selectedWati && varCount > 0 && (
                  <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block">变量映射 · Variable Mapping</label>
                    <div className="space-y-1.5">
                      {(selectedWati.param_names && selectedWati.param_names.length > 0
                        ? selectedWati.param_names
                        : Array.from({ length: varCount }, (_, i) => String(i + 1))
                      ).map((pname, idx) => {
                        const key = String(idx + 1);
                        return (
                          <div key={pname} className="flex items-center gap-2">
                            <span className="font-mono text-xs bg-amber-100 border border-amber-300 text-amber-900 px-2 py-1 rounded-sm min-w-[70px] text-center font-bold">{`{{${pname}}}`}</span>
                            <span className="text-slate-400 text-xs">→</span>
                            <select
                              data-testid={`wati-map-${pname}`}
                              value={mapping[key] || ""}
                              onChange={(e) => setMapping({ ...mapping, [key]: e.target.value })}
                              className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700 bg-white"
                            >
                              <option value="">-- 选择字段 --</option>
                              {BULK_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-2 text-[10px] text-slate-500">
                  🐢 官方模板发送节流 3–5 秒/条，防封号。
                </div>
              </div>
            ) : (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block">Local template</label>
                <select
                  data-testid="bulk-template-select"
                  value={selected?.name || ""}
                  onChange={(e) => {
                    const t = templates.find(t => t.name === e.target.value);
                    setSelected(t || null);
                    setMapping({});
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white"
                >
                  <option value="">-- Select template --</option>
                  {templates.map(t => (
                    <option key={t.name} value={t.name}>
                      {t.name} · {t.category} · {t.status}
                    </option>
                  ))}
                </select>
                {selected && varCount > 0 && (
                  <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block">Variable mapping</label>
                    <div className="space-y-1.5">
                      {Array.from({ length: varCount }, (_, i) => i + 1).map(n => (
                        <div key={n} className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded-sm min-w-[45px] text-center">{`{{${n}}}`}</span>
                          <span className="text-slate-400 text-xs">→</span>
                          <select
                            data-testid={`bulk-map-${n}`}
                            value={mapping[String(n)] || ""}
                            onChange={(e) => setMapping({ ...mapping, [String(n)]: e.target.value })}
                            className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white"
                          >
                            <option value="">-- Choose field --</option>
                            {BULK_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Scheduled send */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block flex items-center gap-1">
                ⏰ 排程发送 (可选 · 留空立即发送)
              </label>
              <input
                data-testid="scheduled-for-input"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white"
              />
              {scheduledFor && (
                <div className="text-[10px] text-emerald-700 mt-1">
                  将于 {new Date(scheduledFor).toLocaleString("zh-CN")} 自动开始发送
                </div>
              )}
            </div>

            {/* Image upload */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 block">附带图片 (可选)</label>
              {image ? (
                <div className="flex items-center gap-3 p-2 border border-slate-200 rounded-sm bg-slate-50">
                  <img src={image.previewUrl} alt="attach" className="w-16 h-16 object-cover rounded-sm border border-slate-200" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{image.filename}</div>
                    <div className="text-[10px] text-slate-500 truncate">{image.path}</div>
                  </div>
                  <button
                    data-testid="bulk-remove-image"
                    onClick={() => setImage(null)}
                    className="text-[10px] px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm font-semibold"
                  >移除</button>
                </div>
              ) : (
                <div>
                  <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="bulk-image-input" />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="bulk-image-btn"
                    className="w-full py-2 border-2 border-dashed border-slate-300 text-xs text-slate-600 rounded-sm hover:border-emerald-500 hover:text-emerald-700 disabled:opacity-50"
                  >
                    {uploading ? "上传中…" : "📎 点击选择图片 (JPG/PNG · 最大 5MB)"}
                  </button>
                </div>
              )}
            </div>

            {/* Live preview */}
            {(currentBody || image) && filteredLeads.length > 0 && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1.5 block">
                  预览 · {filteredLeads[0]?.name || "(no leads)"}
                </label>
                <div data-testid="bulk-preview" className="p-3 bg-emerald-50 border-l-4 border-emerald-500 text-sm text-slate-800 rounded-sm whitespace-pre-wrap leading-relaxed">
                  {image && <img src={image.previewUrl} alt="" className="max-w-[120px] rounded-sm mb-2" />}
                  {previewText || "(无文字)"}
                </div>
              </div>
            )}

            <button
              data-testid="bulk-send-submit"
              onClick={submit}
              disabled={busy || filteredLeads.length === 0}
              className="w-full py-2.5 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              <Send size={13} /> {busy ? "Sending…" : `Send to ${filteredLeads.length} contacts`}
            </button>
          </div>
        )}

        {/* Save-as-template inline dialog */}
        {showSaveTpl && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[60] p-4" onClick={() => setShowSaveTpl(false)}>
            <div className="bg-white border border-slate-200 rounded-sm w-full max-w-sm p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="font-display text-base font-bold mb-3">💾 保存为模板</div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 block">模板名称</label>
              <input
                data-testid="tpl-name-input"
                value={tplName}
                onChange={(e) => setTplName(e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase())}
                placeholder="e.g. sel_promo_aug_zh"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 mb-3"
              />
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 block">类别</label>
              <select
                value={tplCategory}
                onChange={(e) => setTplCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm mb-4 bg-white"
              >
                <option value="MARKETING">MARKETING</option>
                <option value="UTILITY">UTILITY</option>
              </select>
              <div className="flex gap-2">
                <button onClick={() => setShowSaveTpl(false)} className="flex-1 py-2 border border-slate-300 text-sm rounded-sm hover:bg-slate-50">取消</button>
                <button data-testid="tpl-save-confirm" onClick={saveAsTemplate} className="flex-1 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800">保存</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ImportCsvModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [tags, setTags] = useState("");
  const [defaultStage, setDefaultStage] = useState("New");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tags", tags);
    fd.append("default_stage", defaultStage);
    try {
      const { data } = await api.post("/leads-import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`新增 ${data.created} · 合并 ${data.merged}`);
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-md shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">Bulk Import</div>
            <div className="font-display text-lg font-bold">导入 CSV 名单</div>
          </div>
          <button onClick={onClose} data-testid="import-close"><X size={18} /></button>
        </div>
        {result ? (
          <div className="p-5 space-y-3">
            <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-sm space-y-1">
              <div className="text-2xl font-display font-black text-emerald-800">✓ 导入完成</div>
              <div className="text-sm">新增 <b>{result.created}</b> · 合并 <b>{result.merged}</b> · 跳过 <b>{result.skipped}</b></div>
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-32 overflow-y-auto scrollbar-thin text-xs text-red-700 bg-red-50 p-2 rounded-sm">
                {result.errors.slice(0, 20).map((e) => <div key={e}>{e}</div>)}
              </div>
            )}
            <button onClick={onDone} data-testid="import-done" className="w-full py-2.5 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <input
              data-testid="import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0])}
              required
              className="w-full text-sm"
            />
            <input
              data-testid="import-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="批量标签 (逗号分隔) 例如: 历史学员-2025, 教育节旧名单"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700"
            />
            <select data-testid="import-stage" value={defaultStage} onChange={(e) => setDefaultStage(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
              {STAGES.map(s => <option key={s} value={s}>Default stage: {STAGE_LABELS[s] || s}</option>)}
            </select>
            <div className="text-[10px] text-slate-500 bg-slate-50 p-2 rounded-sm leading-relaxed">
              <b>CSV 列 (不区分大小写)</b>: Name, Phone/Mobile, Email, State, Job Role, Organization, Last Course, Interest, Tags, Client Category, Title/Nickname
              <br /><b>手机列别名</b>: Phone, Mobile, Phone Number, WhatsApp, 电话, 手机 均可 (自动去空格/横线)
              <br /><b>去重规则</b>: 以 Phone 为主键。已存在的会自动合并空白字段+追加标签,不会破坏 Kanban 进度。
            </div>
            <button data-testid="import-submit" type="submit" disabled={busy || !file} className="w-full py-2.5 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-40">
              {busy ? "Importing…" : "开始导入"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function AddLeadModal({ onClose, onCreated }) {
  const [meta, setMeta] = useState({ states: [], job_roles: [], client_categories: [] });
  const [form, setForm] = useState({
    name: "", phone: "", email: "", title: "", organization: "",
    state: "", job_role: "", client_category: "",
    last_inquired_course: "", interest: "", stage: STAGES[0] || "New",
  });
  useEffect(() => { api.get("/lead-meta").then(r => setMeta(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leads", form); toast.success("Lead created"); onCreated(); }
    catch (e) { toast.error(extractErr(e)); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display text-lg font-bold">New lead</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <input data-testid="new-lead-name" required value={form.name} onChange={upd("name")} placeholder="Full name" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          <input data-testid="new-lead-phone" required value={form.phone} onChange={upd("phone")} placeholder="+60..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 font-mono" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="new-lead-title" value={form.title} onChange={upd("title")} placeholder="Title (院长/校长)" className="px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
            <input data-testid="new-lead-organization" value={form.organization} onChange={upd("organization")} placeholder="Organization" className="px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="new-lead-state" value={form.state} onChange={upd("state")} className="px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
              <option value="">State (州属)...</option>
              {meta.states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="new-lead-job-role" value={form.job_role} onChange={upd("job_role")} className="px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
              <option value="">Job Role (岗位)...</option>
              {meta.job_roles.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <select data-testid="new-lead-category" value={form.client_category} onChange={upd("client_category")} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
            <option value="">Client Category (客户类别)...</option>
            {meta.client_categories.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="new-lead-email" value={form.email} onChange={upd("email")} placeholder="Email (optional)" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          <input data-testid="new-lead-last-course" value={form.last_inquired_course} onChange={upd("last_inquired_course")} placeholder="Last inquired course" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          <input data-testid="new-lead-interest" value={form.interest} onChange={upd("interest")} placeholder="General interest" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          <select data-testid="new-lead-stage" value={form.stage} onChange={upd("stage")} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 bg-white">
            {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
          </select>
          <button data-testid="new-lead-submit" type="submit" className="w-full py-2.5 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800">Create lead</button>
        </form>
      </div>
    </div>
  );
}
